import {
  getCurrentUserControllerGetCurrentUserQueryKey,
  levelAtLeast,
  resolveHasFactoryAccess,
  resolveSectionLevel,
  type CurrentUserResponseDto,
  type PermissionSection,
} from '@artic/api-client';
import type { QueryClient } from '@tanstack/react-query';

interface MatchWithStaticData {
  staticData?: {
    permissionSection?: PermissionSection;
    permissionLevel?: 'read' | 'write';
    requiresFactoryAccess?: boolean;
  };
}

/** A minimal stand-in for `ParsedLocation` — only the fields this module reads. */
export interface GuardLocation {
  href: string;
  pathname: string;
}

/** The router's own `matchRoutes`, narrowed to what this module reads off a match. */
export type MatchRoutesFn = (pathname: string) => readonly MatchWithStaticData[];

/**
 * The sections a user can land on. Partner API is never one: its dashboard is
 * on the realtime service, so Neo has no page for it, and a user who can read
 * only Partner API lands on `/no-access`.
 */
type LandingSection = Exclude<SectionRequirement['section'], 'partner_api'>;

/**
 * The first-landing path for each hub section, in sidebar order. Every entry
 * is an unflagged Neo page so it is always reachable once its section is
 * readable, with one exception: `/materials/usage` renders a "not enabled"
 * panel when the `bill_of_materials` feature flag is off, so it stays
 * reachable either way. Factory Access (job cards) comes first because it
 * has no section level of its own — it's an all-or-nothing factory grant,
 * the broadest gate a user can clear.
 */
const SECTION_LANDINGS: readonly (SectionRequirement & { section: LandingSection })[] = [
  { section: 'factory_access' },
  { section: 'quality', level: 'read' },
  { section: 'planner', level: 'read' },
  { section: 'catalog', level: 'read' },
  { section: 'materials', level: 'read' },
  { section: 'cost_to_produce', level: 'read' },
  { section: 'settings', level: 'read' },
];

const LANDING_PATH: Record<LandingSection, string> = {
  factory_access: '/job-cards',
  quality: '/quality-packs',
  planner: '/projects',
  catalog: '/products',
  materials: '/materials/usage',
  cost_to_produce: '/operating-costs',
  settings: '/factories',
};

function requirementAllowed(
  requirement: SectionRequirement,
  queryClient: QueryClient,
  activePartner: string | undefined,
): boolean {
  const response = queryClient.getQueryData<{ data: CurrentUserResponseDto }>(
    getCurrentUserControllerGetCurrentUserQueryKey(),
  );
  const user = response?.data;
  if (!user) return true;
  if (requirement.section === 'factory_access') {
    return resolveHasFactoryAccess(user, activePartner);
  }
  return levelAtLeast(
    resolveSectionLevel(user, activePartner, requirement.section),
    requirement.level,
  );
}

/**
 * The first section landing (in sidebar order) the user can read, or `null`
 * when nothing is. Used both as the deep-link fallback and as the last
 * resort before `/no-access`.
 */
export function firstPermittedPath(
  queryClient: QueryClient,
  activePartner: string | undefined,
): string | null {
  for (const requirement of SECTION_LANDINGS) {
    if (requirementAllowed(requirement, queryClient, activePartner)) {
      return LANDING_PATH[requirement.section];
    }
  }
  return null;
}

/**
 * Where Neo's `/` sends a user: their first readable section landing, or
 * `/no-access` when nothing is. Never loops — none of the landings is `/`,
 * and `/no-access` sits outside every section. Staff and exempt users resolve
 * every section to write, so they always land on the first one.
 */
export function homeRedirectTarget(
  queryClient: QueryClient,
  activePartner: string | undefined,
): string {
  return firstPermittedPath(queryClient, activePartner) ?? '/no-access';
}

/**
 * Where a denied navigation should land. Tries, in order:
 * 1. the previous resolved location, if there was one, it isn't the page
 *    that was just denied, and it's still readable (re-checked — permissions
 *    can change mid-session, so this guards against a redirect loop);
 * 2. the first section landing the user can read (a cold deep link has no
 *    previous location to fall back to);
 * 3. `/no-access`, when nothing in the hub is readable.
 */
export function deniedRedirectTarget({
  queryClient,
  partner,
  deniedHref,
  previous,
  matchRoutes,
}: {
  queryClient: QueryClient;
  partner: string | undefined;
  deniedHref: string;
  previous: GuardLocation | undefined;
  matchRoutes: MatchRoutesFn;
}): string {
  if (previous && previous.href !== deniedHref) {
    const previousMatches = matchRoutes(previous.pathname);
    if (checkSectionAccess(queryClient, partner, previousMatches).ok) {
      return previous.href;
    }
  }
  return homeRedirectTarget(queryClient, partner);
}

/** What a route needs: a level on a section, or access to any factory (the Factory section). */
export type SectionRequirement =
  | { section: PermissionSection; level: 'read' | 'write' }
  | { section: 'factory_access' };

/**
 * The section requirement for a set of route matches, resolved leaf-first so
 * the most-specific route wins. A leaf may raise the level (e.g. `new` routes
 * need write) without restating the section.
 */
export function sectionRequirementFor(
  matches: readonly MatchWithStaticData[],
): SectionRequirement | null {
  let level: 'read' | 'write' | undefined;
  for (let i = matches.length - 1; i >= 0; i--) {
    const data = matches[i]?.staticData;
    level ??= data?.permissionLevel;
    if (data?.permissionSection) {
      return { section: data.permissionSection, level: level ?? 'read' };
    }
    if (data?.requiresFactoryAccess) return { section: 'factory_access' };
  }
  return null;
}

/**
 * Whether the cached current user may enter the matched routes. Reads the
 * `/current-user` query the root `beforeLoad` has just ensured; with no cached
 * user it allows (the root's own auth handling owns that path).
 */
export function checkSectionAccess(
  queryClient: QueryClient,
  activePartner: string | undefined,
  matches: readonly MatchWithStaticData[],
): { ok: true } | { ok: false; section: SectionRequirement['section'] } {
  const requirement = sectionRequirementFor(matches);
  if (!requirement) return { ok: true };
  const response = queryClient.getQueryData<{ data: CurrentUserResponseDto }>(
    getCurrentUserControllerGetCurrentUserQueryKey(),
  );
  const user = response?.data;
  if (!user) return { ok: true };
  if (requirement.section === 'factory_access') {
    return resolveHasFactoryAccess(user, activePartner)
      ? { ok: true }
      : { ok: false, section: 'factory_access' };
  }
  const level = resolveSectionLevel(user, activePartner, requirement.section);
  return levelAtLeast(level, requirement.level)
    ? { ok: true }
    : { ok: false, section: requirement.section };
}

export type GuardResult =
  | { ok: true }
  // `redirectTo` is `null` for a denied PRELOAD: there is no one to toast and
  // nothing to navigate, so the root `beforeLoad` just lets it resolve
  // quietly. A real navigation (`preload: false`) always carries a target.
  | { ok: false; redirectTo: string | null };

/**
 * The root `beforeLoad`'s whole permissions decision, pulled out so it can be
 * exercised without mounting the real route tree (its other concerns —
 * cookies, Intercom, realtime — are heavy to boot in a test). A denied
 * PRELOAD (hovering a link) is silent: no toast, no redirect, since the user
 * hasn't gone anywhere. A denied real navigation queues the toast and resolves
 * a redirect target via `deniedRedirectTarget`.
 */
export function guardSectionAccess({
  queryClient,
  partner,
  matches,
  location,
  preload,
  getPreviousLocation,
  matchRoutes,
}: {
  queryClient: QueryClient;
  partner: string | undefined;
  matches: readonly MatchWithStaticData[];
  location: GuardLocation;
  preload: boolean;
  getPreviousLocation: () => GuardLocation | undefined;
  matchRoutes: MatchRoutesFn;
}): GuardResult {
  if (checkSectionAccess(queryClient, partner, matches).ok) return { ok: true };
  if (preload) return { ok: false, redirectTo: null };

  queueAccessDeniedToast();
  const redirectTo = deniedRedirectTarget({
    queryClient,
    partner,
    deniedHref: location.href,
    previous: getPreviousLocation(),
    matchRoutes,
  });
  return { ok: false, redirectTo };
}

// ── Pending toast ──
//
// The root `beforeLoad` runs before the sonner <Toaster> mounts on a cold
// deep-link, and sonner drops toasts sent before a Toaster subscribes. The
// guard queues the toast here; <AccessDeniedToast> (rendered after <Toaster>)
// fires it once mounted, and again on every later denial.

type Listener = () => void;
let pending = false;
const listeners = new Set<Listener>();

export function queueAccessDeniedToast(): void {
  pending = true;
  for (const listener of listeners) listener();
}

export function takePendingAccessDeniedToast(): boolean {
  const was = pending;
  pending = false;
  return was;
}

export function subscribeAccessDeniedToast(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
